import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock, Users2, UserCheck, CircleDollarSign, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import AdminHeader from "@/components/AdminHeader";
import { toast } from "sonner";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
}

function Chip({ children, tone = "emerald" }) {
  const map = {
    emerald: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
    red: "bg-red-500/10 text-red-400 border-red-500/30",
    slate: "bg-white/5 text-white/70 border-white/10",
  };
  return (
    <span className={`inline-block text-[11px] font-semibold px-2 py-1 rounded-full border ${map[tone]}`}>
      {children}
    </span>
  );
}

export default function AdminUserDetail() {
  const { code } = useParams();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/admin/users/${encodeURIComponent(code)}`);
        setDetail(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [code]);

  return (
    <div className="min-h-screen text-white" style={{ background: "#050506" }}>
      <AdminHeader title="User Record" />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <Link
          to="/admin/dashboard"
          data-testid="admin-back-link"
          className="inline-flex items-center gap-1 text-emerald-400 text-sm font-semibold"
        >
          <ArrowLeft size={14} /> Back to dashboard
        </Link>

        {loading ? (
          <div className="py-24 flex justify-center">
            <div className="h-8 w-8 rounded-full border-4 border-emerald-400 border-t-transparent animate-spin" />
          </div>
        ) : !detail ? (
          <div className="text-white/60">User not found.</div>
        ) : (
          <>
            <section className="rounded-2xl border border-white/10 bg-gradient-to-br from-zinc-900 to-black p-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="h-16 w-16 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-400 flex items-center justify-center font-black text-2xl">
                  {detail.user.name?.slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="text-2xl font-extrabold text-white"
                        style={{ fontFamily: "Outfit, sans-serif" }}>
                      {detail.user.name}
                    </h1>
                    {detail.user.activated
                      ? <Chip tone="emerald">Active</Chip>
                      : <Chip tone="red">Inactive</Chip>}
                    <Chip tone="slate">{detail.user.rank}</Chip>
                  </div>
                  <div className="text-emerald-400 font-mono text-sm mt-1">
                    {detail.user.user_code}
                  </div>
                </div>
              </div>

              <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="rounded-xl border border-white/10 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-white/60">Email</div>
                  <div className="text-sm mt-1 truncate">{detail.user.email || "—"}</div>
                </div>
                <div className="rounded-xl border border-white/10 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-white/60">Phone</div>
                  <div className="text-sm mt-1">{detail.user.phone || "—"}</div>
                </div>
                <div className="rounded-xl border border-white/10 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-white/60">Sponsor</div>
                  <div className="text-sm mt-1 truncate">
                    {detail.user.sponsor_name || "—"}
                  </div>
                  <div className="text-[11px] text-emerald-400 font-mono">
                    {detail.user.sponsor_user_code || "—"}
                  </div>
                </div>
                <div className="rounded-xl border border-white/10 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-white/60">Joined</div>
                  <div className="text-sm mt-1">{formatDate(detail.user.created_at)}</div>
                </div>
              </div>
            </section>

            <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatMini icon={Users2} label="Direct Referrals" value={detail.team.direct} tint="bg-emerald-500/25" />
              <StatMini icon={UserCheck} label="Total Team" value={detail.team.total} tint="bg-teal-500/25" />
              <StatMini icon={ShieldCheck} label="Active Team" value={detail.team.active} tint="bg-amber-500/25" />
              <StatMini icon={CircleDollarSign} label="Direct Income"
                        value={`₹${Number(detail.wallet.direct_income).toLocaleString("en-IN")}`}
                        tint="bg-violet-500/25" />
            </section>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* History timeline */}
              <section className="rounded-2xl border border-white/10 bg-zinc-950/60">
                <div className="p-4 border-b border-white/10">
                  <h2 className="text-lg font-bold text-emerald-400 flex items-center gap-2"
                      style={{ fontFamily: "Outfit, sans-serif" }}>
                    <Clock size={16} /> Activity History
                  </h2>
                </div>
                <ol data-testid="user-history-list" className="p-4 space-y-3">
                  {detail.history.map((h, i) => (
                    <li key={`${h.event}-${h.at || i}`} className="flex gap-3">
                      <div className="mt-1.5 h-2 w-2 rounded-full bg-emerald-400 shrink-0" />
                      <div>
                        <div className="text-sm font-semibold text-white">{h.event}</div>
                        <div className="text-xs text-white/70">{h.detail}</div>
                        <div className="text-[11px] text-white/50 mt-0.5">{formatDate(h.at)}</div>
                      </div>
                    </li>
                  ))}
                  {detail.history.length === 0 && (
                    <li className="text-sm text-white/60 text-center py-6">No activity yet.</li>
                  )}
                </ol>
              </section>

              {/* Direct referrals list */}
              <section className="rounded-2xl border border-white/10 bg-zinc-950/60">
                <div className="p-4 border-b border-white/10">
                  <h2 className="text-lg font-bold text-emerald-400 flex items-center gap-2"
                      style={{ fontFamily: "Outfit, sans-serif" }}>
                    <Users2 size={16} /> Direct Referrals ({detail.directs.length})
                  </h2>
                </div>
                <ul data-testid="user-directs-list" className="divide-y divide-white/5">
                  {detail.directs.map((d) => (
                    <li key={d.user_code} className="p-4 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-semibold text-white truncate">{d.name}</div>
                        <div className="text-xs text-emerald-400 font-mono">{d.user_code}</div>
                        <div className="text-[11px] text-white/50">{formatDate(d.created_at)}</div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {d.activated
                          ? <Chip tone="emerald">Active</Chip>
                          : <Chip tone="red">Inactive</Chip>}
                        <Link
                          to={`/admin/users/${encodeURIComponent(d.user_code)}`}
                          className="text-xs text-emerald-400 hover:text-emerald-300 font-semibold"
                        >
                          Open
                        </Link>
                      </div>
                    </li>
                  ))}
                  {detail.directs.length === 0 && (
                    <li className="p-6 text-center text-sm text-white/60">
                      No direct referrals yet.
                    </li>
                  )}
                </ul>
              </section>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function StatMini({ icon: Icon, label, value, tint }) {
  return (
    <div className="rounded-2xl border border-white/10 p-4 bg-gradient-to-br from-zinc-900 to-black relative overflow-hidden">
      <div className={`absolute -right-6 -top-6 h-20 w-20 rounded-full blur-2xl ${tint}`} />
      <div className="relative flex items-center gap-2 text-white/70 text-xs uppercase tracking-widest font-semibold">
        <Icon size={12} className="text-emerald-400" /> {label}
      </div>
      <div className="relative mt-2 text-2xl font-extrabold text-white"
           style={{ fontFamily: "Outfit, sans-serif" }}>
        {value}
      </div>
    </div>
  );
}
